/**
 * The coast's parts: the beach's sand, sea, posts and marram, which the code beach and the villa
 * (ADR-037) both lay. Here, not in beach.ts, so the code beach can go (W12) and leave the villa whole.
 * Built round ADR-032's stage (8.5 m), kept as it was (ADR-061).
 */
import * as THREE from 'three';
import {
  STAGE_ADR032,
  type BirdsBlock,
  type GrassBlock,
  type WaterBlock,
} from '@vrlearning/lesson-schema';
import { fbm, random } from '../look/noise.js';
import { withCover } from './cover.js';
import { blockFeatures } from './blocks/features.js';
import { buildWaterBlock } from './blocks/water.js';
import { GULL_FLIGHTS } from './gulls.js';
import type { Tuft } from './grass.js';
import { textureFile } from './manifest.js';
import { Parts } from './parts.js';
import { band, grid, lines, ramp } from './terrain.js';
import { tintFor } from './tint.js';
import type { WorldBuilder, WorldDefinition } from './world.js';

const smooth = THREE.MathUtils.smoothstep;
const ripple = fbm(91, 4);
const duneNoise = fbm(92, 5);

export const BEACH = {
  /** The sea at low and high tide. */
  low: -1.5,
  high: -0.55,
  /** How the beach falls to the sea: metres down per metre out, from `from` metres ahead. */
  slope: 0.07,
  from: 9,
} as const;

/** Where the waterline is at a level: metres ahead (−z). */
export const waterlineAt = (level: number): number => BEACH.from - level / BEACH.slope;

/** The beach, sea bed and dunes: flat on the stage, straight along x. */
export function beachHeight(x: number, z: number): number {
  const r = Math.hypot(x, z);
  if (r <= STAGE_ADR032.floor) return 0;
  const ahead = -z;
  // Down to the sea, dead straight at the waterline (rule 2), a gentle bar farther out.
  const fall = ahead > BEACH.from ? -(ahead - BEACH.from) * BEACH.slope : 0;
  const deeper = -Math.max(0, ahead - 150) * 0.03;
  // Dunes behind: a ridge from 22 m, 7–14 m high, wandering a little; blown sand at its foot.
  const behind = z;
  const ridge = 22 + (duneNoise(x / 60, 3) - 0.5) * 14;
  const tall = 8 + 7 * duneNoise(x / 45 + 11, 7);
  const dune =
    tall * smooth(behind, ridge - 10, ridge + 6) * (0.85 + 0.3 * duneNoise(x / 12, z / 12));
  // Dry sand's ripples, only above the high-water line.
  const dry = smooth(fall, BEACH.high - 0.1, BEACH.high + 0.4);
  const ripples = (ripple(x / 3, z / 3) - 0.5) * 0.12 * ramp(r, 9.5, 14) * dry;
  return fall + deeper + dune + ripples;
}

/**
 * The land the beach's builders lay (ADR-037: the villa stands on the same sand, lifted onto a
 * dune): its height, where its low-water line is (`base`: 0 on the beach, the villa's beach lies 6 m
 * under its plateau), whether the flat stage has a ground of its own, and where the marram's grey-
 * green covers the sand.
 */
export type Land = {
  readonly height: (x: number, z: number) => number;
  /** Added to BEACH's levels: the sea, the wet sand, the posts. */
  readonly base: number;
  /** The 6 m stage's own flat ground within the apron (the beach's), a hole in the land for it. */
  readonly stage: boolean;
  /** Marram's grey-green over the sand, 0–1. */
  readonly cover: (x: number, z: number, y: number) => number;
  /** Its meshes' names start with this. */
  readonly name: string;
};

export const BEACH_LAND: Land = {
  height: beachHeight,
  base: 0,
  stage: true,
  // Marram's grey-green on the dunes above 3 m, patchy.
  cover: (x, z, y) =>
    z > 12 ? smooth(y, 2.5, 6) * (0.4 + 0.5 * smooth(duneNoise(x / 9, z / 9), 0.35, 0.6)) : 0,
  name: 'beach',
};

/** The beach's sand, dunes and stage; the villa's dune is the same sand (ADR-037). */
export function sand(world: WorldBuilder, land: Land = BEACH_LAND): void {
  const average = textureFile('coast_sand_01', 'diff', world.manifest).averageColour ?? '#82725b';
  const tint = tintFor('#d4c3a0', average);
  if (land.stage) {
    const dry = world.surface({
      asset: 'coast_sand_01',
      maps: ['diff', 'nor_gl'],
      tint,
      vertexColors: true,
    });
    const stage = new THREE.Mesh(
      band({
        inner: 0,
        outer: STAGE_ADR032.floor,
        rings: 14,
        segments: 96,
        height: () => 0,
        tile: 2.5,
      }),
      dry,
    );
    stage.name = `${land.name}-stage`;
    world.addGround(stage);
  }

  // Wet sand below the high-water line, darker; the dunes' tops grassed over.
  const high = land.base + BEACH.high;
  const tone = (x: number, z: number, y: number): THREE.Color => {
    const wet = 1 - smooth(y, high - 0.15, high + 0.25);
    return new THREE.Color(1, 1, 1).lerp(new THREE.Color(0.62, 0.6, 0.58), wet);
  };
  const beach = world.surface({
    asset: 'coast_sand_01',
    maps: ['diff', 'nor_gl'],
    tint,
    vertexColors: true,
    overlay: { asset: 'aerial_grass_rock', tile: 8, tint: 0xd6dcb0 },
  });
  beach.polygonOffset = true;
  beach.polygonOffsetFactor = 2;
  beach.polygonOffsetUnits = 2;
  const ground = new THREE.Mesh(
    withCover(
      grid({
        xs: lines(-1500, 1500, 1.5, 40, 1.14),
        zs: lines(-1500, 1500, 1, 60, 1.12),
        height: land.height,
        tile: 2.5,
        tint: tone,
        ...(land.stage ? { hole: STAGE_ADR032.floor } : {}),
      }),
      land.cover,
    ),
    beach,
  );
  ground.name = `${land.name}-land`;
  world.addDressing(ground);
}

/** Where marram grows: `clumps` clumps placed by `place`, a stalk kept where `keep` says. */
export type MarramField = {
  readonly height: (x: number, z: number) => number;
  readonly clumps: number;
  readonly place: (next: () => number) => [number, number];
  readonly keep: (x: number, z: number, y: number) => boolean;
  /** Stalks per clump, spread, stalk height, width and shade: the beach's when absent. */
  readonly tuft?: {
    readonly perClump: readonly [number, number];
    readonly spread: number;
    readonly height: readonly [number, number];
    readonly width: readonly [number, number];
    readonly shade: readonly [number, number];
  };
};

const BEACH_TUFT = {
  perClump: [3, 8],
  spread: 2.4,
  height: [0.55, 0.95],
  width: [0.5, 0.9],
  shade: [1.35, 1.75],
} as const;

/** The beach's: behind it, on the dunes from 1.8 m up, clear of the apron. */
export const BEACH_MARRAM: MarramField = {
  height: beachHeight,
  clumps: 1500,
  place: (next) => [(next() - 0.5) * 240, 14 + next() * 26],
  keep: (x, z, y) => y >= 1.8 && Math.hypot(x, z) >= STAGE_ADR032.clear + 1.5,
};

/** The beach's marram as a grass block's settings (the beach package says the same). */
export const BEACH_MARRAM_BLOCK: GrassBlock = {
  type: 'grass',
  id: 'helm',
  area: [
    [-120, 14],
    [120, 14],
    [120, 40],
    [-120, 40],
  ],
  clumps: 1500,
  perClump: [3, 8],
  spread: 2.4,
  height: [0.55, 0.95],
  width: [0.5, 0.9],
  shade: [1.35, 1.75],
  above: 1.8,
  root: '#6c7550',
  tip: '#c4c8a0',
  seed: 9101,
  sway: 0.12,
};

/** The beach's gulls as a birds block's settings. */
export const BEACH_GULLS_BLOCK: BirdsBlock = {
  type: 'birds',
  id: 'meeuwen',
  flights: GULL_FLIGHTS.map((flight) => ({ ...flight })),
  size: 1.5,
  colour: '#f4f3ee',
  emissive: '#6e6e6a',
};

/** Marram grass on the dunes: tall, pale, in clumps, from 3 m up. */
export function marram(seed = 9101, field: MarramField = BEACH_MARRAM): Tuft[] {
  const next = random(seed);
  const tufts: Tuft[] = [];
  for (let clump = 0; clump < field.clumps; clump++) {
    const [ox, oz] = field.place(next);
    const t = field.tuft ?? BEACH_TUFT;
    const per = t.perClump[0] + Math.floor(next() * (t.perClump[1] - t.perClump[0] + 1));
    for (let k = 0; k < per; k++) {
      const x = ox + (next() - 0.5) * t.spread;
      const z = oz + (next() - 0.5) * t.spread;
      const y = field.height(x, z);
      if (!field.keep(x, z, y)) continue;
      tufts.push({
        pos: [x, y - 0.04, z],
        height: t.height[0] + (t.height[1] - t.height[0]) * next(),
        width: t.width[0] + (t.width[1] - t.width[0]) * next(),
        turn: Math.round(next() * 360),
        shade: t.shade[0] + (t.shade[1] - t.shade[0]) * next(),
      });
    }
  }
  return tufts;
}

/** Where the posts run: from `from` to `to` metres ahead, on this land; the beach's pole and lookout with them or not. */
export type PostRows = {
  readonly land: Pick<Land, 'height' | 'base'>;
  readonly from: number;
  readonly to: number;
  /** The beach pole and the lifeguard's lookout, which stand on the beach's own sand. */
  readonly beachFurniture: boolean;
};

export const BEACH_POSTS: PostRows = { land: BEACH_LAND, from: 12, to: 80, beachFurniture: true };

/** Rows of old oak posts running out into the sea, and the beach pole. */
export function posts(world: WorldBuilder, rows: PostRows = BEACH_POSTS): void {
  const parts = new Parts();
  const next = random(9102);
  const { height: bedAt, base } = rows.land;
  for (const x of [-58, 64, -190, 205]) {
    for (let ahead = rows.from; ahead < rows.to; ahead += 1.1) {
      const z = -ahead;
      const bed = bedAt(x, z);
      const top = base + 0.9 - (ahead - rows.from) * 0.02 + (next() - 0.5) * 0.25;
      const height = top - bed + 0.3;
      parts.cylinder(
        'oak',
        0.14 + 0.03 * next(),
        height,
        [x + (next() - 0.5) * 0.15, bed - 0.3 + height / 2, z],
        0x4a3f36,
        7,
      );
    }
  }
  if (rows.beachFurniture) beachFurniture(parts);
  const meshes = parts.meshes({
    oak: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95 }),
    ...(rows.beachFurniture
      ? {
          pole: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6 }),
          hut: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7 }),
          glass: new THREE.MeshStandardMaterial({
            vertexColors: true,
            roughness: 0.1,
            metalness: 0.5,
          }),
        }
      : {}),
  });
  for (const mesh of meshes) {
    mesh.name = `${world.slug}-${mesh.name}`;
    world.addDressing(mesh);
  }
}

/** The beach pole and a lifeguard's lookout, on the beach's own sand. */
function beachFurniture(parts: Parts): void {
  // The beach pole (strandpaal), red and white, a little left of ahead at the dunes' foot.
  const [px, pz] = [-16, 13];
  const base = beachHeight(px, pz);
  for (let k = 0; k < 8; k++) {
    parts.cylinder(
      'pole',
      0.13,
      0.4,
      [px, base + 0.2 + k * 0.4, pz],
      k % 2 ? 0xf2efe8 : 0xc0332b,
      10,
    );
  }
  // A lifeguard's lookout on stilts, far to the right.
  const [lx, lz] = [42, 6];
  const ground = beachHeight(lx, lz);
  for (const [dx, dz] of [
    [-0.9, -0.9],
    [0.9, -0.9],
    [-0.9, 0.9],
    [0.9, 0.9],
  ] as const) {
    parts.cylinder('oak', 0.08, 3, [lx + dx, ground + 1.5, lz + dz], 0xd9d4c8, 6);
  }
  parts.box('hut', [2.4, 2.2, 2.4], [lx, ground + 4.1, lz], 0xe9e5dc);
  parts.box('hut', [2.8, 0.15, 2.8], [lx, ground + 5.28, lz], 0xc0332b);
  parts.box('glass', [2.42, 0.8, 2.42], [lx, ground + 4.5, lz], 0x2a3238);
}

/** The sea: its bed, its level at low water, where the surf breaks then (z), and whether it has a tide. */
export type SeaPlan = {
  readonly bed: (x: number, z: number) => number;
  readonly low: number;
  readonly shore: number;
  /** The beach's `getij` feature (eb/vloed); a world without it keeps the sea at low water. */
  readonly tide: boolean;
};

export const BEACH_SEA: SeaPlan = {
  bed: beachHeight,
  low: BEACH.low,
  shore: -waterlineAt(BEACH.low),
  tide: true,
};

/** The sea as a water block's settings (the beach package says the same). */
export function seaBlock(plan: SeaPlan = BEACH_SEA): WaterBlock {
  // A straight shore along x at low tide, carried up the beach with the tide (the water block's surf).
  return {
    type: 'water',
    id: 'zee',
    level: plan.low,
    radius: 1600,
    deep: '#2a4648',
    shallow: '#5b8a80',
    waves: 1,
    foam: true,
    surf: {
      shore: Array.from({ length: 121 }, (_, i) => [-600 + i * 10, plan.shore] as [number, number]),
      closed: false,
    },
  };
}

export function sea(
  world: WorldBuilder,
  towardsSun: THREE.Vector3,
  horizon: string,
  plan: SeaPlan = BEACH_SEA,
): void {
  const block = seaBlock(plan);
  const water = buildWaterBlock(
    world,
    block,
    { ground: plan.bed, towardsSun, horizon, name: world.slug },
    plan.tide ? [BEACH.low, BEACH.high] : [plan.low, plan.low],
  );
  if (!plan.tide) return;

  // The tide (lesson-schema's WORLDS: eb, vloed): in or out over 8 s when the
  // class moves on by one step, the surf climbing the beach with it.
  const mid = (waterlineAt(BEACH.low) + waterlineAt(BEACH.high)) / 2;
  const anchor = new THREE.Mesh(
    new THREE.CylinderGeometry(9, 9, BEACH.high - BEACH.low + 0.6, 12).translate(
      0,
      (BEACH.low + BEACH.high) / 2,
      0,
    ),
    new THREE.MeshBasicMaterial(),
  );
  anchor.position.set(0, 0, -mid);
  anchor.visible = false;
  anchor.name = 'beach-tide';
  world.addDressing(anchor);
  blockFeatures(
    world,
    {
      getij: {
        initial: 'eb',
        seconds: 8,
        states: { eb: { 'zee.level': BEACH.low }, vloed: { 'zee.level': BEACH.high } },
        anchor,
      },
    },
    new Map([['zee', water]]),
  );
}

/** The beach's sky, sun, haze and glow: the beach's and the villa's (ADR-037). */
export const BEACH_LOOK = {
  sky: {
    hdri: 'kloofendal_48d_partly_cloudy_puresky',
    seen: true,
    exposure: 1,
    environment: 0.8,
    yaw: 0,
    drift: 0.9,
  },
  sun: { intensity: 2.7, colour: 0xfff4e4 },
  fog: { density: 0.0017 },
  glow: 1.6,
} as const satisfies Pick<WorldDefinition, 'sky' | 'sun' | 'fog' | 'glow'>;
